import { Component, Vue } from 'vue-property-decorator'

import { library } from '@fortawesome/fontawesome-svg-core'
import {
	faPlusCircle,
	faExternalLinkAlt,
	faFilePdf,
	faStarHalfStroke,
	faPenClip,
	faFilter,
	faSearch,
	faBan,
} from '@fortawesome/free-solid-svg-icons'

import { faBlackTie } from '@fortawesome/free-brands-svg-icons'

import Loader from '@/components/Loader/index.vue'
import CardGroup from '@/components/CardGroup/index.vue'
import fetch from '@/helpers/fetch'

import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import { Validations } from 'vuelidate-property-decorators'

library.add(
	faPlusCircle,
	faExternalLinkAlt,
	faFilePdf,
	faStarHalfStroke,
	faPenClip,
	faBlackTie,
	faFilter,
	faSearch,
	faBan,
)

interface AlphabetItem {
	showValue: string
	searchValues: string[]
	matchValue: string
}

@Component({
	components: {
		Loader,
		CardGroup,
	},
	mixins: [validationMixin],
})
export default class Groups extends Vue {
	allGroupsListClosed: null | any[] = null
	allGroupsList: null | any[] = null

	location = window.location

	filters = {
		elaborator: '',
		sales: '',
		reviewer: '',
		clientActive: true,
		clientInactive: true,
	}

	elaborators: string[] = []
	reviewers: string[] = []
	sales: string[] = []

	view = 'alphabet'

	searchQuery = ''
	alphabetList: any = null
	alphabetListSearch: any = null
	alphabetListKeys: any[] | null = null

	modalLoading = false
	errorMessage = ''

	data() {
		return {
			name: '',
		}
	}

	@Validations()
	validations() {
		return {
			name: {
				required,
			},
		}
	}

	async save(e) {
		e.preventDefault()
		this.modalLoading = true

		this.errorMessage = ''

		const that: any = this
		const name = that.name
		const resp = await fetch.post('/api/groups', { name })

		if (resp.ok) {
			this.$bvModal.hide('modal-create-group')
			this.$router.push({
				name: 'GroupsDetail',
				params: { clientId: resp.id },
			})
		} else {
			this.errorMessage = resp.error
		}
		this.modalLoading = false
	}

	async mounted() {
		this.$store.dispatch('saveCurrentFullPath', this.$route.fullPath)

		this.allGroupsList = (await fetch.get('/api/groups')).data

		this.alphabetList = createAlphabetList(this.allGroupsList)
		this.alphabetListSearch = createAlphabetList(this.allGroupsList)
		this.alphabetListKeys = Object.keys(this.alphabetList)

		this.elaborators = createElaboratorList(this.allGroupsList)
		this.reviewers = createReviewerList(this.allGroupsList)
		this.sales = createSalesList(this.allGroupsList)
	}

	onHover($event) {
		$event.preventDefault()
		const letter = $event.target.id
		this.cleanSearch()
		this.cleanFilters()
		location.hash = letter.replace('letter-', '')
	}

	onFilterChange($event, filterName) {
		this.cleanSearch()
		if (filterName === 'clientInactive' && !this.filters.clientActive) {
			this.filters.clientActive = true
		}

		if (filterName === 'clientActive' && !this.filters.clientInactive) {
			this.filters.clientInactive = true
		}
		const searchList = filterGroups(this.filters, this.allGroupsList)
		this.alphabetList = createAlphabetList(searchList)
		this.alphabetListSearch = createAlphabetList(searchList)
	}

	onSearchChange($event) {
		this.cleanFilters()
		if (this.searchQuery) {
			const searchList = this.allGroupsList?.filter((group) => {
				return group?.name
					.toLowerCase()
					.includes(this.searchQuery.toLowerCase())
			})
			this.alphabetListSearch = createAlphabetList(searchList)
		}
	}

	cleanSearch() {
		this.searchQuery = ''
	}

	cleanFilters() {
		this.filters.sales = ''
		this.filters.elaborator = ''
		this.filters.reviewer = ''
	}

	search(group, searchQuery) {
		const searchQueryLowerCase = removeAccents(
			searchQuery.toLowerCase().trim(),
		)

		const nameLowerCase = removeAccents(group?.name.toLowerCase())
		if (searchQueryLowerCase.includes(' ')) {
			const searchQueryArray = searchQueryLowerCase.split(' ')
			let result = false

			for (const query of searchQueryArray) {
				if (nameLowerCase.includes(query)) {
					result = true
				} else {
					result = false
					break
				}
			}
			return result
		}

		return nameLowerCase.includes(searchQueryLowerCase)
	}

	isDisabled(array) {
		if (!array || !array.length) {
			return true
		}
		return false
	}
}

function removeAccents(str) {
	return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

function createAlphabetList(allGroupsList) {
	const object = {}

	allGroupsList.forEach((group) => {
		const letter = removeAccents(group?.name?.[0].toUpperCase())
		object[letter] = object[letter] || []
		object[letter].push(group)
	})

	return object
}

function createElaboratorList(allGroupsList) {
	const uniqueEmailsObj = {}

	allGroupsList.forEach((group) => {
		const judEmail = group?.elaborator?.email
		const admEmail =
			group?.groupsInfoAdministratives?.[0]?.userByElaboratorUserId?.email

		if (judEmail) {
			uniqueEmailsObj[judEmail] = true
		}

		if (admEmail) {
			uniqueEmailsObj[admEmail] = true
		}
	})

	return Object.keys(uniqueEmailsObj)
}

function createReviewerList(allGroupsList) {
	const uniqueEmailsObj = {}

	allGroupsList.forEach((group) => {
		const judEmail = group?.reviewer?.email
		const admEmail =
			group?.groupsInfoAdministratives?.[0]?.userByReviewerUserId?.email

		if (judEmail) {
			uniqueEmailsObj[judEmail] = true
		}

		if (admEmail) {
			uniqueEmailsObj[admEmail] = true
		}
	})

	return Object.keys(uniqueEmailsObj)
}

function createSalesList(allGroupsList) {
	const uniqueEmailsObj = {}

	allGroupsList.forEach((group) => {
		const judEmail = group?.reportSendingFrom?.email
		const admEmail =
			group?.groupsInfoAdministratives?.[0]?.userByReportsendingfromUserId
				?.email

		if (judEmail) {
			uniqueEmailsObj[judEmail] = true
		}

		if (admEmail) {
			uniqueEmailsObj[admEmail] = true
		}
	})

	return Object.keys(uniqueEmailsObj)
}

function filterGroups(filters, allGroupsList) {
	const filtered = allGroupsList.filter((group) => {
		const enable = group.groupsRelations.reduce(
			(final, item) => {
				if (item.trial !== null) {
					if (item.trial.closed === null || !item.trial.closed) {
						final.trial = true
					}
				}

				if (item.trialAdm !== null) {
					if (
						item.trialAdm.closed === null ||
						!item.trialAdm.closed
					) {
						final.trialAdm = true
					}
				}
				return final
			},
			{ trial: false, trialAdm: false },
		)

		const isClientInactive = !enable.trial && !enable.trialAdm
		const isClientActive = !isClientInactive
		const isClientInactiveChecked = filters?.clientInactive
		const isClientActiveChecked = filters?.clientActive

		// Verificar se o grupo atende aos critérios de cliente ativo e inativo
		const clientActiveFilterPassed =
			(isClientActiveChecked && isClientActive) ||
			(isClientInactiveChecked && isClientInactive)

		// Verificar se o grupo atende aos critérios de filtro de elaborator
		const elaboratorFilterPassed =
			filters?.elaborator?.trim() === '' ||
			filters?.elaborator === group?.elaborator?.email ||
			filters?.elaborator ===
				group?.groupsInfoAdministratives?.[0]?.userByElaboratorUserId
					?.email

		// Verificar se o grupo atende aos critérios de filtro de sales
		const salesFilterPassed =
			filters?.sales?.trim() === '' ||
			filters?.sales === group?.reportSendingFrom?.email ||
			filters?.sales ===
				group?.groupsInfoAdministratives?.[0]
					?.userByReportsendingfromUserId?.email

		// Verificar se o grupo atende aos critérios de filtro de reviewer
		const reviewerFilterPassed =
			filters?.reviewer?.trim() === '' ||
			filters?.reviewer === group?.reviewer?.email ||
			filters?.reviewer ===
				group?.groupsInfoAdministratives?.[0]?.userByReviewerUserId
					?.email

		// Retornar verdadeiro se todos os critérios forem atendidos
		return (
			clientActiveFilterPassed &&
			elaboratorFilterPassed &&
			salesFilterPassed &&
			reviewerFilterPassed
		)
	})

	return filtered
}
